<template>
    <div class="pa-4">
        <v-card class="mt-2 py-2">
            <v-card-title class="pb-2">
                <v-row>
                    <v-col cols="12" md="3">
                        <span>Filter Outlet</span>
                        <v-select
                            v-model="filterOutlet"
                            :items="outlets"
                            item-text="name"
                            item-value="code"
                            outlined
                            dense
                            hide-details="auto"
                            clearable
                            placeholder="Semua Outlet"
                        />
                    </v-col>
                    <v-col cols="12" md="3">
                        <span>Filter Varian Botol</span>
                        <v-select
                            v-model="filterVolume"
                            :items="botols"
                            item-text="description"
                            item-value="code"
                            outlined
                            dense
                            hide-details="auto"
                            clearable
                            placeholder="Semua Varian"
                        />
                    </v-col>
                    <v-col cols="12" md="3">
                        <span>Dari Tanggal</span>
                        <v-menu
                            v-model="filterDateMenuFrom"
                            :close-on-content-click="true"
                            :nudge-right="40"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    v-model="filterDateFrom"
                                    prepend-inner-icon="mdi-calendar"
                                    readonly
                                    v-bind="attrs"
                                    v-on="on"
                                    hide-details="auto"
                                    dense
                                    outlined
                                    clearable
                                    placeholder="Pilih Tanggal"
                                />
                            </template>
                            <v-date-picker v-model="filterDateFrom" />
                        </v-menu>
                    </v-col>
                    <v-col cols="12" md="3">
                        <span>Sampai Tanggal</span>
                        <v-menu
                            v-model="filterDateMenuTo"
                            :close-on-content-click="true"
                            :nudge-right="40"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    v-model="filterDateTo"
                                    prepend-inner-icon="mdi-calendar"
                                    readonly
                                    v-bind="attrs"
                                    v-on="on"
                                    hide-details="auto"
                                    dense
                                    outlined
                                    clearable
                                    placeholder="Pilih Tanggal"
                                />
                            </template>
                            <v-date-picker v-model="filterDateTo" :min="filterDateFrom" />
                        </v-menu>
                    </v-col>
                </v-row>
            </v-card-title>
        </v-card>
        <v-data-table
            :headers="headers"
            :items="filteredItems"
            :search="search"
            class="elevation-1 mt-2"
            :loading="isLoading"
        >
            <template v-slot:top>
                <v-toolbar flat>
                    <v-toolbar-title>Closing Outlet</v-toolbar-title>
                    <v-divider class="mx-4" inset vertical />
                    <v-text-field
                        v-model="search"
                        append-icon="mdi-magnify"
                        label="Search"
                        single-line
                        hide-details
                    />
                    <v-spacer />
                    <v-btn
                        color="primary"
                        dark
                        class="mb-2"
                        small
                        @click="openAddDialog"
                        v-if="$store.state.currentPagePriv.create"
                    >
                        <v-icon small>mdi-plus</v-icon>
                        ADD
                    </v-btn>
                    <v-dialog v-model="dialogDelete" max-width="500px">
                        <v-card>
                            <v-card-title class="text-h5">Konfirmasi</v-card-title>
                            <v-card-text>Anda akan menghapus closing outlet ini?</v-card-text>
                            <v-card-actions>
                                <v-spacer />
                                <v-btn color="grey" text @click="dialogDelete = false">Cancel</v-btn>
                                <v-btn color="red" text @click="deleteItemConfirm">Delete</v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-dialog>
                </v-toolbar>
            </template>
            <template v-slot:[`item.closing_date`]="{ item }">
                {{ formatDate(item.closing_date) }}
            </template>
            <template v-slot:[`item.outlet_code`]="{ item }">
                {{ getOutletName(item.outlet_code) }}
            </template>
            <template v-slot:[`item.volume`]="{ item }">
                {{ getBotolDescription(item.volume) }}
            </template>
            <template v-slot:[`item.sales_tomorrow`]="{ item }">
                {{ item.sales_tomorrow || 0 }}
            </template>
            <template v-slot:[`item.cautions`]="{ item }">
                {{ item.cautions || 0 }}
            </template>
            <template v-slot:[`item.new_arrival`]="{ item }">
                {{ item.new_arrival || 0 }}
            </template>
            <template v-slot:[`item.frozen`]="{ item }">
                {{ item.frozen || 0 }}
            </template>
            <template v-slot:[`item.actions`]="{ item }">
                <v-icon small class="mr-2" @click="viewDetail(item)">
                    mdi-eye
                </v-icon>
                <v-icon small class="mr-2" @click="editItem(item)" v-if="$store.state.currentPagePriv.update">
                    mdi-pencil
                </v-icon>
                <v-icon small @click="deleteItem(item)" v-if="$store.state.currentPagePriv.delete">
                    mdi-delete
                </v-icon>
            </template>
            <template v-slot:no-data>
                <v-btn color="primary" @click="initialize">
                    Reset
                </v-btn>
            </template>
        </v-data-table>

        <!-- Add/Edit Dialog -->
        <v-dialog v-model="dialog" max-width="500px">
            <v-card>
                <v-card-title>
                    <span class="text-h5">{{ formTitle }}</span>
                </v-card-title>
                <v-card-text>
                    <v-container>
                        <v-row>
                            <v-col cols="12">
                                <span class="font-weight-bold">Nama Outlet <span class="red--text">*</span></span>
                                <v-select
                                    v-model="editedItem.outlet_code"
                                    :items="outlets"
                                    item-text="name"
                                    item-value="code"
                                    outlined
                                    dense
                                    hide-details="auto"
                                    :disabled="editedIndex > -1"
                                    :error-messages="validationErrors.outlet_code"
                                    @change="clearError('outlet_code')"
                                />
                            </v-col>
                            <v-col cols="12" v-if="editedIndex === -1">
                                <span class="font-weight-bold">Tanggal <span class="red--text">*</span></span>
                                <v-text-field
                                    v-model="formattedClosingDate"
                                    prepend-inner-icon="mdi-calendar"
                                    readonly
                                    hide-details="auto"
                                    dense
                                    outlined
                                    :error-messages="validationErrors.closing_date"
                                    disabled
                                />
                            </v-col>
                            <v-col cols="12" v-if="editedIndex === -1">
                                <span class="font-weight-bold">Variant Bottle / Volume <span class="red--text">*</span></span>
                                <v-select
                                    v-model="editedItem.volume"
                                    :items="botols"
                                    item-text="description"
                                    item-value="code"
                                    outlined
                                    dense
                                    hide-details="auto"
                                    :error-messages="validationErrors.volume"
                                    @change="clearError('volume')"
                                />
                            </v-col>
                            <v-col cols="12">
                                <span class="font-weight-bold">Jual Besok</span>
                                <v-text-field
                                    v-model.number="editedItem.sales_tomorrow"
                                    type="number"
                                    outlined
                                    dense
                                    hide-details="auto"
                                />
                            </v-col>
                            <v-col cols="12">
                                <span class="font-weight-bold">Hati - Hati</span>
                                <v-text-field
                                    v-model.number="editedItem.cautions"
                                    type="number"
                                    outlined
                                    dense
                                    hide-details="auto"
                                />
                            </v-col>
                            <v-col cols="12">
                                <span class="font-weight-bold">Baru Datang</span>
                                <v-text-field
                                    v-model.number="editedItem.new_arrival"
                                    type="number"
                                    outlined
                                    dense
                                    hide-details="auto"
                                />
                            </v-col>
                            <v-col cols="12">
                                <span class="font-weight-bold">Barang Beku</span>
                                <v-text-field
                                    v-model.number="editedItem.frozen"
                                    type="number"
                                    outlined
                                    dense
                                    hide-details="auto"
                                />
                            </v-col>
                            <v-col cols="12" v-if="!isInnerCity">
                                <span class="font-weight-bold">Jual Hari Ini</span>
                                <v-text-field
                                    v-model.number="editedItem.sales_today"
                                    type="number"
                                    outlined
                                    dense
                                    hide-details="auto"
                                />
                            </v-col>
                            <v-col cols="12" v-if="!isInnerCity">
                                <span class="font-weight-bold">Barang Beku H-1</span>
                                <v-text-field
                                    v-model.number="editedItem.frozen_h1"
                                    type="number"
                                    outlined
                                    dense
                                    hide-details="auto"
                                />
                            </v-col>
                            <v-col cols="12" v-if="!isInnerCity">
                                <span class="font-weight-bold">Barang Beku Khusus Sabtu</span>
                                <v-text-field
                                    v-model.number="editedItem.frozen_saturday"
                                    type="number"
                                    outlined
                                    dense
                                    hide-details="auto"
                                />
                            </v-col>
                        </v-row>
                    </v-container>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn color="grey" text @click="close">CANCEL</v-btn>
                    <v-btn color="primary" text @click="save">SAVE</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- View Dialog -->
        <v-dialog v-model="dialogDetail" max-width="500px">
            <v-card>
                <v-card-title>
                    <span class="text-h5">Closing Outlet Detail</span>
                </v-card-title>
                <v-card-text>
                    <v-container>
                        <v-row>
                            <v-col cols="12">
                                <span class="font-weight-bold">Nama Outlet</span>
                                <v-text-field
                                    :value="getOutletName(viewedItem.outlet_code)"
                                    outlined
                                    dense
                                    readonly
                                    hide-details="auto"
                                />
                            </v-col>
                            <v-col cols="12">
                                <span class="font-weight-bold">Tanggal</span>
                                <v-text-field
                                    :value="formatDate(viewedItem.closing_date)"
                                    outlined
                                    dense
                                    readonly
                                    hide-details="auto"
                                />
                            </v-col>
                            <v-col cols="12">
                                <span class="font-weight-bold">Variant Bottle / Volume</span>
                                <v-text-field
                                    :value="getBotolDescription(viewedItem.volume)"
                                    outlined
                                    dense
                                    readonly
                                    hide-details="auto"
                                />
                            </v-col>
                            <v-col cols="12">
                                <span class="font-weight-bold">Jual Besok</span>
                                <v-text-field
                                    :value="viewedItem.sales_tomorrow || 0"
                                    outlined
                                    dense
                                    readonly
                                    hide-details="auto"
                                />
                            </v-col>
                            <v-col cols="12">
                                <span class="font-weight-bold">Hati - Hati</span>
                                <v-text-field
                                    :value="viewedItem.cautions || 0"
                                    outlined
                                    dense
                                    readonly
                                    hide-details="auto"
                                />
                            </v-col>
                            <v-col cols="12">
                                <span class="font-weight-bold">Baru Datang</span>
                                <v-text-field
                                    :value="viewedItem.new_arrival || 0"
                                    outlined
                                    dense
                                    readonly
                                    hide-details="auto"
                                />
                            </v-col>
                            <v-col cols="12">
                                <span class="font-weight-bold">Barang Beku</span>
                                <v-text-field
                                    :value="viewedItem.frozen || 0"
                                    outlined
                                    dense
                                    readonly
                                    hide-details="auto"
                                />
                            </v-col>
                            <v-col cols="12" v-if="viewedItem.sales_today !== undefined">
                                <span class="font-weight-bold">Jual Hari Ini</span>
                                <v-text-field
                                    :value="viewedItem.sales_today || 0"
                                    outlined
                                    dense
                                    readonly
                                    hide-details="auto"
                                />
                            </v-col>
                            <v-col cols="12" v-if="viewedItem.frozen_h1 !== undefined">
                                <span class="font-weight-bold">Barang Beku H-1</span>
                                <v-text-field
                                    :value="viewedItem.frozen_h1 || 0"
                                    outlined
                                    dense
                                    readonly
                                    hide-details="auto"
                                />
                            </v-col>
                            <v-col cols="12" v-if="viewedItem.frozen_saturday !== undefined">
                                <span class="font-weight-bold">Barang Beku Khusus Sabtu</span>
                                <v-text-field
                                    :value="viewedItem.frozen_saturday || 0"
                                    outlined
                                    dense
                                    readonly
                                    hide-details="auto"
                                />
                            </v-col>
                        </v-row>
                    </v-container>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn color="primary" text @click="dialogDetail = false">Close</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-snackbar top v-model="saveError" color="red accent-2" :timeout="5000">
            {{ errorMessage }}
            <template v-slot:action="{ attrs }">
                <v-btn color="white" text v-bind="attrs" @click="saveError = false">
                    Close
                </v-btn>
            </template>
        </v-snackbar>

        <v-snackbar top v-model="saveSuccess" color="green accent-2" :timeout="5000">
            {{ successMessage }}
            <template v-slot:action="{ attrs }">
                <v-btn color="white" text v-bind="attrs" @click="saveSuccess = false">
                    Close
                </v-btn>
            </template>
        </v-snackbar>
    </div>
</template>

<script>
import moment from 'moment';

export default {
    data() {
        return {
            search: '',
            dialog: false,
            dialogDelete: false,
            dialogDetail: false,
            isLoading: false,
            dateMenu: false,
            saveError: false,
            saveSuccess: false,
            errorMessage: '',
            successMessage: '',
            headers: [
                { text: 'Tanggal', value: 'closing_date', sortable: true },
                { text: 'Nama Outlet', value: 'outlet_code', sortable: true },
                { text: 'Variant Bottle / Volume', value: 'volume', sortable: true },
                { text: 'Jual Besok', value: 'sales_tomorrow', sortable: true },
                { text: 'Hati - Hati', value: 'cautions', sortable: true },
                { text: 'Baru Datang', value: 'new_arrival', sortable: true },
                { text: 'Barang Beku', value: 'frozen', sortable: true },
                { text: 'Actions', value: 'actions', sortable: false },
            ],
            items: [],
            outlets: [],
            botols: [],
            editedIndex: -1,
            editedItem: {
                outlet_code: '',
                closing_date: moment().format('YYYY-MM-DD'),
                volume: '',
                sales_tomorrow: 0,
                cautions: 0,
                sales_today: 0,
                new_arrival: 0,
                frozen: 0,
                frozen_h1: 0,
                frozen_saturday: 0,
            },
            defaultItem: {
                outlet_code: '',
                closing_date: moment().format('YYYY-MM-DD'),
                volume: '',
                sales_tomorrow: 0,
                cautions: 0,
                sales_today: 0,
                new_arrival: 0,
                frozen: 0,
                frozen_h1: 0,
                frozen_saturday: 0,
            },
            viewedItem: {},
            selectedOutlet: null,
            filterOutlet: null,
            filterVolume: null,
            filterDateFrom: null,
            filterDateTo: null,
            filterDateMenuFrom: false,
            filterDateMenuTo: false,
            validationErrors: {
                outlet_code: [],
                closing_date: [],
                volume: [],
            },
        }
    },

    computed: {
        formTitle() {
            return this.editedIndex === -1 ? 'Add New Closing Outlet' : 'Edit Closing Outlet'
        },
        formattedClosingDate() {
            return this.editedItem.closing_date ? moment(this.editedItem.closing_date).format('DD MMMM YYYY') : ''
        },
        isInnerCity() {
            if (this.editedIndex > -1) {
                // For edit mode, find outlet from items
                const outletCode = this.editedItem.outlet_code;
                const outlet = this.outlets.find(o => o.code === outletCode);
                return outlet ? outlet.is_inner_city : false;
            }
            return this.selectedOutlet ? this.selectedOutlet.is_inner_city : false;
        },
        filteredItems() {
            let filtered = this.items;
            
            if (this.filterOutlet) {
                filtered = filtered.filter(item => item.outlet_code === this.filterOutlet);
            }
            
            if (this.filterVolume) {
                filtered = filtered.filter(item => item.volume === this.filterVolume);
            }
            
            if (this.filterDateFrom || this.filterDateTo) {
                filtered = filtered.filter(item => {
                    const itemDate = moment(item.closing_date).format('YYYY-MM-DD');
                    
                    if (this.filterDateFrom && this.filterDateTo) {
                        // Filter by range
                        return itemDate >= this.filterDateFrom && itemDate <= this.filterDateTo;
                    } else if (this.filterDateFrom) {
                        // Filter from date only
                        return itemDate >= this.filterDateFrom;
                    } else if (this.filterDateTo) {
                        // Filter to date only
                        return itemDate <= this.filterDateTo;
                    }
                    return true;
                });
            }
            
            return filtered;
        }
    },

    watch: {
        dialog(val) {
            val || this.close()
        },
        'editedItem.outlet_code'(val) {
            if (val && this.editedIndex === -1) {
                const outlet = this.outlets.find(o => o.code === val);
                if (outlet) {
                    this.selectedOutlet = outlet;
                }
            }
        }
    },

    async created() {
        await this.loadMasterData();
        this.initialize();
    },

    methods: {
        async loadMasterData() {
            try {
                // Load outlets
                try {
                    const outletResponse = await this.$store.dispatch('outlet/getObjs');
                    if (outletResponse.data.status === 'success') {
                        this.outlets = outletResponse.data.data || [];
                    }
                } catch (error) {
                    console.warn('Cannot load outlets:', error);
                    this.outlets = [];
                }

                // Load botols
                let dataMiscCodes = this.$store.state.misccode.objs || [];
                const allowedBotolCodes = ['100', '250', '350', '490'];
                this.botols = dataMiscCodes
                    .filter(i => i.type.includes('Botol') && allowedBotolCodes.includes(i.code))
                    .sort((a, b) => parseInt(a.code) - parseInt(b.code));

                // Set default volume
                if (this.botols.length > 0) {
                    this.editedItem.volume = this.botols.find(b => b.code === '490')?.code || this.botols[0].code;
                }
            } catch (error) {
                console.error('Error loading master data:', error);
            }
        },

        async initialize() {
            this.isLoading = true;
            try {
                const response = await this.$store.dispatch('outletclosing/getObjs', {
                    apidomain: 'outlet-closings',
                    parameter: ''
                });

                if (response.data.status === 'success') {
                    this.items = response.data.data || [];
                }
            } catch (error) {
                console.error('Error loading closing outlet data:', error);
                this.errorMessage = 'Gagal memuat data closing outlet';
                this.saveError = true;
            } finally {
                this.isLoading = false;
            }
        },

        openAddDialog() {
            this.editedIndex = -1;
            this.editedItem = Object.assign({}, this.defaultItem);
            this.editedItem.closing_date = moment().format('YYYY-MM-DD');
            this.selectedOutlet = null;
            this.clearAllErrors();
            this.dialog = true;
        },

        editItem(item) {
            this.editedIndex = this.items.indexOf(item);
            this.editedItem = Object.assign({}, item);
            const outlet = this.outlets.find(o => o.code === item.outlet_code);
            if (outlet) {
                this.selectedOutlet = outlet;
            }
            this.dialog = true;
        },

        viewDetail(item) {
            this.viewedItem = Object.assign({}, item);
            this.dialogDetail = true;
        },

        deleteItem(item) {
            this.editedIndex = this.items.indexOf(item);
            this.editedItem = Object.assign({}, item);
            this.dialogDelete = true;
        },

        async deleteItemConfirm() {
            try {
                const response = await this.$store.dispatch('outletclosing/deleteObj', this.editedItem.id);
                if (response.data.status === 'success') {
                    this.items.splice(this.editedIndex, 1);
                    this.successMessage = 'Data closing outlet berhasil dihapus';
                    this.saveSuccess = true;
                } else {
                    this.errorMessage = response.data.message || 'Gagal menghapus data';
                    this.saveError = true;
                }
            } catch (error) {
                console.error('Error deleting closing outlet:', error);
                this.errorMessage = error.response?.data?.message || 'Gagal menghapus data closing outlet';
                this.saveError = true;
            } finally {
                this.dialogDelete = false;
            }
        },

        close() {
            this.dialog = false;
            this.$nextTick(() => {
                this.editedItem = Object.assign({}, this.defaultItem);
                this.editedIndex = -1;
                this.selectedOutlet = null;
                this.clearAllErrors();
            });
        },

        clearError(field) {
            this.validationErrors[field] = [];
        },

        clearAllErrors() {
            this.validationErrors = {
                outlet_code: [],
                closing_date: [],
                volume: [],
            };
        },

        validateForm() {
            this.clearAllErrors();
            let isValid = true;

            if (!this.editedItem.outlet_code) {
                this.validationErrors.outlet_code = ['Nama Outlet wajib diisi'];
                isValid = false;
            }

            if (!this.editedItem.closing_date) {
                this.validationErrors.closing_date = ['Tanggal wajib diisi'];
                isValid = false;
            }

            if (!this.editedItem.volume) {
                this.validationErrors.volume = ['Variant Bottle / Volume wajib diisi'];
                isValid = false;
            }

            return isValid;
        },

        async save() {
            // Validate form
            if (!this.validateForm()) {
                this.errorMessage = 'Mohon lengkapi semua field yang wajib diisi';
                this.saveError = true;
                return;
            }

            try {
                if (this.editedIndex > -1) {
                    // Update existing
                    const response = await this.$store.dispatch('outletclosing/putObj', {
                        id: this.editedItem.id,
                        payload: {
                            sales_tomorrow: this.editedItem.sales_tomorrow || 0,
                            cautions: this.editedItem.cautions || 0,
                            sales_today: this.editedItem.sales_today || 0,
                            new_arrival: this.editedItem.new_arrival || 0,
                            frozen: this.editedItem.frozen || 0,
                            frozen_h1: this.editedItem.frozen_h1 || 0,
                            frozen_saturday: this.editedItem.frozen_saturday || 0,
                        }
                    });

                    if (response.data.status === 'success') {
                        Object.assign(this.items[this.editedIndex], this.editedItem);
                        this.successMessage = 'Data closing outlet berhasil diperbarui';
                        this.saveSuccess = true;
                        this.close();
                    } else {
                        this.errorMessage = response.data.message || 'Gagal memperbarui data';
                        this.saveError = true;
                    }
                } else {
                    // Create new
                    const response = await this.$store.dispatch('outletclosing/postObj', {
                        items: [{
                            outlet_code: this.editedItem.outlet_code,
                            closing_date: this.editedItem.closing_date,
                            volume: this.editedItem.volume,
                            sales_tomorrow: this.editedItem.sales_tomorrow || 0,
                            cautions: this.editedItem.cautions || 0,
                            sales_today: this.editedItem.sales_today || 0,
                            new_arrival: this.editedItem.new_arrival || 0,
                            frozen: this.editedItem.frozen || 0,
                            frozen_h1: this.editedItem.frozen_h1 || 0,
                            frozen_saturday: this.editedItem.frozen_saturday || 0,
                        }]
                    });

                    if (response.data.status === 'success') {
                        this.successMessage = 'Data closing outlet berhasil disimpan';
                        this.saveSuccess = true;
                        this.close();
                        await this.initialize(); // Reload data
                    } else {
                        this.errorMessage = response.data.message || 'Gagal menyimpan data';
                        this.saveError = true;
                    }
                }
            } catch (error) {
                console.error('Error saving closing outlet:', error);
                
                // Handle Laravel validation errors
                if (error.response?.data?.errors) {
                    const backendErrors = error.response.data.errors;
                    
                    // Map backend validation errors to form fields
                    if (backendErrors['items.0.outlet_code']) {
                        this.validationErrors.outlet_code = backendErrors['items.0.outlet_code'];
                    }
                    if (backendErrors['items.0.closing_date']) {
                        this.validationErrors.closing_date = backendErrors['items.0.closing_date'];
                    }
                    if (backendErrors['items.0.volume']) {
                        this.validationErrors.volume = backendErrors['items.0.volume'];
                    }
                    if (backendErrors.outlet_code) {
                        this.validationErrors.outlet_code = backendErrors.outlet_code;
                    }
                    if (backendErrors.closing_date) {
                        this.validationErrors.closing_date = backendErrors.closing_date;
                    }
                    if (backendErrors.volume) {
                        this.validationErrors.volume = backendErrors.volume;
                    }
                    
                    this.errorMessage = error.response?.data?.message || 'Terdapat kesalahan validasi pada form';
                } else {
                    this.errorMessage = error.response?.data?.message || 'Gagal menyimpan data closing outlet';
                }
                
                this.saveError = true;
            }
        },

        getOutletName(code) {
            if (!code) return '-';
            const outlet = this.outlets.find(o => o.code === code);
            return outlet ? outlet.name : code;
        },

        getBotolDescription(code) {
            if (!code) return '-';
            const botol = this.botols.find(b => b.code === code);
            return botol ? botol.description : code;
        },

        formatDate(date) {
            if (!date) return '-';
            return moment(date).format('DD MMM YYYY');
        }
    }
}
</script>
